import React, { HTMLAttributes } from "react";
import { SingleLineLookupFields } from "helpers/merchant/lookup/single-line-lookup-fields";
import { ImageRowData } from "../financial-target.types";
import "./title-cell-renderer.css";
import { DisplayedGridRows } from "reducers/collection-target/types";
import { FINANCIAL_TARGET_ROW_IDS } from "../constants";
import { Tooltip, withStyles } from "@material-ui/core";
import { formatMessage } from "lib/globalize-util";

const fieldRenderer = (text: string, index: number) => {
    const formattedText = text.replace(";", "-");

    if (index > 0) {
        return <span> | {formattedText}</span>;
    }
    return <span>{formattedText}</span>;
};

const CustomTooltip = withStyles((theme) => ({
    tooltip: {
        textAlign: "left",
        fontSize: "12px",
    },
}))(Tooltip);

export const TitleCellRenderer = ({
    value,
    displayedRows,
    targetInfo,
}: {
    value: string;
    displayedRows: DisplayedGridRows;
    targetInfo: ImageRowData;
}) => {
    const parentRef = React.useRef(null);
    const childRef = React.useRef(null);
    const [isTooltipEnabled, setIsTooltipEnabled] = React.useState(false);

    React.useEffect(() => {
        if (childRef.current.scrollHeight > parentRef.current.offsetHeight) {
            setIsTooltipEnabled(true);
        } else {
            setIsTooltipEnabled(false);
        }
    }, [displayedRows]);

    const targetName = targetInfo?.dependenciesValues && (
        <SingleLineLookupFields
            fieldRenderer={fieldRenderer}
            schemaList={targetInfo?.schema}
            dependenciesValues={targetInfo.dependenciesValues}
            merchantId={targetInfo.merchantId}
        />
    );

    const renderTooltipTitle = (description) => (
        <>
            <div>{targetName}</div>
            <div>{description}</div>
        </>
    );

    const titleStyles: HTMLAttributes<"div">["style"] = isTooltipEnabled
        ? { whiteSpace: "nowrap" }
        : { whiteSpace: "normal" };

    return (
        <div className="title-cell-container">
            <div ref={parentRef} className="title-cell-title-container">
                <div ref={childRef}>
                    <CustomTooltip
                        title={renderTooltipTitle(value)}
                        disableHoverListener={!isTooltipEnabled}
                        disableFocusListener={!isTooltipEnabled}
                        disableTouchListener={!isTooltipEnabled}
                        style={{ fontSize: "12px" }}
                    >
                        <div className="title-cell-title" style={titleStyles}>
                            {targetName}
                            {isTooltipEnabled && "..."}
                        </div>
                    </CustomTooltip>
                    {!isTooltipEnabled && <div className="title-cell-description">{value}</div>}
                </div>
            </div>
            <div className="title-cell-targets">
                {displayedRows[FINANCIAL_TARGET_ROW_IDS.TARGET] && <div>{formatMessage("Target $")}</div>}
                {displayedRows[FINANCIAL_TARGET_ROW_IDS.TOTAL] && <div>{formatMessage("Working Total $")}</div>}
                {displayedRows[FINANCIAL_TARGET_ROW_IDS.VARIANCE] && <div>{formatMessage("Variance $")}</div>}
                {displayedRows[FINANCIAL_TARGET_ROW_IDS.VARIANCE_PERCENTAGE] && (
                    <div>{formatMessage("Variance %")}</div>
                )}
            </div>
        </div>
    );
};
